import { useChains } from "@opensea/app-kit";
import { graphql, useQuery } from "@opensea/graphql";
import { useBlurAuthToken } from "../../../../blur";
import { ActionTimelineFragment } from "../../../../components/ActionTimeline/ActionTimeline.fragment";
import { NETWORK_ONLY_OPERATION_CONTEXT } from "../../../../constants";
import { useAccount } from "../../../../polychain/hooks/useAccount";
import { useSetBlockchainActionErrors } from "../../../hooks/useSetBlockchainActionErrors";
import { useSetBlockchainActionErrorsFragment } from "../../../hooks/useSetBlockchainActionErrors.fragment";
const BuyItemQuery = graphql(`
    query BuyItemQuery(
      $address: Address!
      $blurAuthToken: String
      $itemQuantities: [ItemQuantityInput!]!
      $recipient: Address
      $substituteItems: Boolean
      $slippage: Float
    ) {
      buyItems(
        address: $address
        blurAuthToken: $blurAuthToken
        itemQuantities: $itemQuantities
        recipient: $recipient
        substituteItems: $substituteItems
        slippage: $slippage
      ) {
        actions {
          ...ActionTimeline
        }
        errors {
          ...useSetBlockchainActionErrors
        }
      }
    }
  `, [ActionTimelineFragment, useSetBlockchainActionErrorsFragment]);

// TODO(devcon hackathon): Consolidate to a single query
const BuyItemCrossChainQuery = graphql(`
    query BuyItemCrossChainQuery(
      $address: Address!
      $recipient: Address
      $slippage: Float
      $fromAssets: [AssetQuantityInput!]!
      $toAssets: [AssetQuantityInput!]!
    ) {
      swap(
        address: $address
        recipient: $recipient
        slippageTolerance: $slippage
        fromAssets: $fromAssets
        toAssets: $toAssets
      ) {
        actions {
          ...ActionTimeline
        }
        errors {
          ...useSetBlockchainActionErrors
        }
      }
    }
  `, [ActionTimelineFragment, useSetBlockchainActionErrorsFragment]);
export const useBuyItemQuery = ({
  itemQuantities,
  recipient,
  pause,
  slippage,
  currency,
  supportsCrossChain
}) => {
  const {
    address
  } = useAccount();
  const blurAuthToken = useBlurAuthToken();
  const {
    getNativeCurrency
  } = useChains();
  const variables = {
    address: address,
    blurAuthToken,
    itemQuantities,
    recipient,
    slippage,
    substituteItems: Boolean(slippage && slippage > 0)
  };

  // Use cross-chain when paying with any currency OTHER than the target chain's native currency
  // This includes same-chain ERC20s and different-chain tokens
  const targetChain = itemQuantities.length > 0 ? itemQuantities[0].item.chain : undefined;
  const nativeCurrency = targetChain ? getNativeCurrency(targetChain) : undefined;
  const isNativeCurrencyOnTargetChain = currency?.contractAddress === nativeCurrency?.contractAddress && currency?.chainIdentifier === targetChain;
  const shouldUseCrossChain = !!(currency && supportsCrossChain && targetChain && !isNativeCurrencyOnTargetChain);
  const [response, refetch] = useQuery({
    query: BuyItemQuery,
    variables,
    pause: !address || pause || shouldUseCrossChain,
    context: NETWORK_ONLY_OPERATION_CONTEXT
  });

  // Only use cross-chain query when both currency is provided AND cross-chain is supported
  const [responseCrossChain, refetchCrossChain] = useQuery({
    query: BuyItemCrossChainQuery,
    variables: {
      address: address,
      fromAssets: [{
        asset: {
          contractAddress: currency?.contractAddress ?? "",
          chain: currency?.chainIdentifier ?? ""
        }
      }],
      toAssets: itemQuantities.map(itemQuantity => ({
        asset: {
          contractAddress: itemQuantity.item.contractAddress,
          chain: itemQuantity.item.chain,
          tokenId: itemQuantity.item.tokenId
        },
        quantity: itemQuantity.quantity.toString()
      })),
      slippage,
      recipient
    },
    pause: !address || pause || !shouldUseCrossChain,
    context: NETWORK_ONLY_OPERATION_CONTEXT
  });

  // TODO: This will be cleaned up once we consolidate to a single buyItem query
  const actions = shouldUseCrossChain ? responseCrossChain.data?.swap.actions : response.data?.buyItems.actions;
  const errors = shouldUseCrossChain ? responseCrossChain.data?.swap.errors : response.data?.buyItems.errors;
  const error = shouldUseCrossChain ? responseCrossChain.error : response.error;
  const fetching = shouldUseCrossChain ? responseCrossChain.fetching : response.fetching;
  useSetBlockchainActionErrors(errors);
  return {
    actions: actions ?? [],
    error,
    fetching,
    refetch: () => {
      if (shouldUseCrossChain) {
        return refetchCrossChain({
          requestPolicy: "network-only"
        });
      }
      return refetch({
        requestPolicy: "network-only"
      });
    }
  };
};